import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { PageLoader } from '../../ui/PageLoader'
import { loginPath } from '../basePath'
import { ensureFreshToken, hasCachedAccount, restoreSilently } from '../oaseToken'
import { useSession } from '../session'
import { signOut } from '../signOut'

export interface RequireAuthProps {
  children: ReactNode
}

/**
 * The auth gate: children render once the session has a token that
 * ensureFreshToken() accepted (refreshed if < 10 min left). Without a token
 * but with a cached MSAL account it first tries a silent sign-in, once
 * (restoreSilently, as the portal's checkAuth). Otherwise, or when that or
 * the refresh fails, it redirects to /login?next=<this path>.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { token } = useSession()
  const location = useLocation()
  const [isReady, setIsReady] = useState(false)
  // One silent attempt per mount (restoreSilently is single-flight): a token
  // that is lost again afterwards goes to /login.
  const [hasRestored, setRestored] = useState(false)
  const canRestore = !token && !hasRestored && hasCachedAccount()

  useEffect(() => {
    if (!canRestore) return
    let isCurrent = true
    void restoreSilently().finally(() => {
      // A new token re-renders this gate through the session store.
      if (isCurrent) setRestored(true)
    })
    return () => {
      isCurrent = false
    }
  }, [canRestore])

  useEffect(() => {
    if (!token || isReady) return
    let isCurrent = true
    ensureFreshToken().then(
      () => {
        if (isCurrent) setIsReady(true)
      },
      () => {
        // The session is over: clearing it re-renders this gate into <Navigate>.
        void signOut({ reason: 'expired', redirect: false })
      }
    )
    return () => {
      isCurrent = false
    }
  }, [token, isReady])

  if (!token && canRestore) {
    return <PageLoader label="Signing in" />
  }
  if (!token) {
    return <Navigate to={loginPath(location.pathname + location.search + location.hash)} replace />
  }
  if (!isReady) {
    return <PageLoader />
  }
  return children
}
